import toast from "react-hot-toast";
import { getMetrics } from "../../../services/calculations/EntityCalculations";
import { getGroupData, getPageTooltips } from "../../../services/GroupContext";
import { getEntityInputCaps } from "../../../utils/adjustedCapsUtil";
import { generateCAPIdByCompName } from "../../../utils/capProcessor";
import { processDateJS } from "../../../utils/dateProcessor";
import {
  addColumnValues,
  addEntityInputSheetProtection,
  addEntityInputTableStyles,
  addNoteToCell,
  createExcelDocument,
  extractEntityInputCapsFromExcel,
  freezeTopRowAndColumnA,
  getListSumFormula,
  getRangeSumFormula,
  getWorksheetFromFile,
  saveExcelFile,
  setNegativeWholeNumberValidation,
  setNumberValidation,
  setPercentageValidation,
  setPositiveFourDecimalNumberValidation,
  setWholeNumberValidation,
} from "./DataImportUtils";

const sheetBuilder = [
  { label: "Company name", type: "header", capMetricId: "company_name" },
  {
    label: "Accounting Period Start Date",
    capMetricId: "start_date",
    type: "date",
  },
  {
    label: "Accounting Period End Date",
    capMetricId: "end_date",
    type: "date",
  },
  { label: "Date joined group", compMetricId: "date_join", type: "date" },
  { label: "Date left group", compMetricId: "date_left", type: "date" },

  { label: "" },

  {
    label: "Currency",
    editable: true,
    type: "currency",
    capMetricId: "currency",
    editable: true,
  },
  {
    label: "Exchange rate (1 GBP = X Currency)",
    editable: true,
    type: "number",
    capMetricId: "fx_rate",
    editable: true,
  },
  {
    label: "Enable OneSource Extraction",
    type: "boolean",
    capMetricId: "onesource_sync",
  },

  { label: "" },

  { label: "Net tax-interest expense/(income) section", type: "header" },

  { label: "" },

  { label: "Non-trading loan relationships", type: "header" },
  {
    label: "Total per Computation Schedule for period",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "nltr_total_per_source_sheet_for_period",
  },
  { label: "Less: Included in the above", type: "italic" },
  {
    label: "Exclude Exchange gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_exclude_exchange_gains_losses",
  },
  {
    label: "Exclude Impairment gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_exclude_impairment_gains_losses",
  },
  {
    label: "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_exclude_tax_interest_income_double_relief",
  },
  {
    label:
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_adjustment_amortised_cost_basis",
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_adjustment_qualifying_election",
  },
  {
    label:
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_exclude_defined_benefit_pension",
  },
  {
    label:
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_bank_expenses",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_adjustment_gaap_transition",
  },
  {
    label: "Adjustment for finance costs associated with leases",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_adjustment_lease_finance_cost",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "nltr_other_adjustments_3",
  },
  {
    label: "Non-trading loan relationships - Subtotal",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 14, end: 27 } },
  },

  { label: "" },

  { label: "Non-trading derivative contracts", type: "header" },
  {
    label: "Total per Computation Schedule for period",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_total_per_source_sheet_for_period",
  },
  { label: "Less: Included in the above", type: "italic" },
  {
    label: "Exclude Exchange gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_exclude_exchange_gains_losses",
  },
  {
    label: "Exclude Impairment gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_exclude_impairment_gains_losses",
  },
  {
    label: "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_exclude_tax_interest_income_double_relief",
  },
  {
    label:
      "Exclude amounts on contracts arising in the ordinary course of trade",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_exclude_ordinary_trade",
  },
  {
    label:
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    editable: true,
    type: "wholeNumber",
    capMetricId:
      "ntdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_adjustment_qualifying_election",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_adjustment_gaap_transition",
  },
  {
    label:
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_adjustment_disregard_regulations",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ntdc_other_adjustments_3",
  },
  {
    label: "Non-trading derivative contracts - subtotal",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 31, end: 43 } },
  },

  { label: "" },

  { label: "Trading loan relationships", type: "header" },
  {
    label: "Total per Computation Schedule for period",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "tlr_total_per_source_sheet_for_period",
  },
  { label: "Less: Included in the above", type: "italic" },
  {
    label: "Exclude Exchange gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_exclude_exchange_gains_losses",
  },
  {
    label: "Exclude Impairment gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_exclude_impairment_gains_losses",
  },
  {
    label: "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_exclude_tax_interest_income_double_relief",
  },
  {
    label:
      "Adjustment for election for amortised cost basis for fair valued creditor relationships",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_adjustment_amortised_cost_basis",
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_adjustment_qualifying_election",
  },
  {
    label:
      "Exclude defined benefit pension income/expense included on loan relationships statement",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_exclude_defined_benefit_pension",
  },
  {
    label:
      "Add bank charges and similar interest type expenses not included in the total above (i.e. not in Loan Relationships schedule)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_bank_expenses",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_adjustment_gaap_transition",
  },
  {
    label: "Adjustment for finance costs associated with leases",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_adjustment_lease_finance_cost",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tlr_other_adjustments_3",
  },
  {
    label: "Trading loan relationships - Subtotal",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 47, end: 60 } },
  },

  { label: "" },

  { label: "Trading derivative contracts", type: "header" },
  {
    label: "Total per Computation Schedule for period",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "tdc_total_per_source_sheet_for_period",
  },
  { label: "Less: Included in the above", type: "italic" },
  {
    label: "Exclude Exchange gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_exclude_exchange_gains_losses",
  },
  {
    label: "Exclude Impairment gains/(losses)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_exclude_impairment_gains_losses",
  },
  {
    label: "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_exclude_tax_interest_income_double_relief",
  },
  {
    label:
      "Exclude amounts on contracts arising in the ordinary course of trade",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_exclude_ordinary_trade",
  },
  {
    label:
      "Exclude amounts for derivatives without an underlying subject matter specified in s384(4) / s387(4)",
    editable: true,
    type: "wholeNumber",
    capMetricId:
      "tdc_exclude_derivatives_without_subject_matters_384(4)_s387(4)",
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_adjustment_qualifying_election",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_adjustment_gaap_transition",
  },
  {
    label:
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_adjustment_disregard_regulations",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tdc_other_adjustments_3",
  },
  {
    label: "Trading derivative contracts - subtotal",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 64, end: 76 } },
  },

  { label: "" },

  { label: "Other financing arrangements", type: "header" },
  {
    label: "Total Per Source Sheet For Period",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "ofa_total_per_source_sheet_for_period",
  },
  { label: "Less: Included in the above", type: "italic" },
  {
    label: "Exclude Tax-Interest Income subject to Double Tax Relief - s388",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_exclude_tax_interest_income_double_relief",
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_adjustment_qualifying_election",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_adjustment_gaap_transition",
  },
  {
    label:
      "Adjustment to include Expense (income) associated with finance leases",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_adjustment_finance_lease",
  },
  {
    label:
      "Adjustment to include Expense (income) associated with debt factoring transactions",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_adjustment_debt_factoring(4)_s387(4)",
  },
  {
    label:
      "Adjustment to include Expense (income) associated with service concession arrangement if and to the extent accounted for as a financial liability",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_adjustment_service_concession",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "ofa_other_adjustments_3",
  },
  {
    label: "Other financing arrangements - subtotal",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 80, end: 90 } },
  },

  { label: "" },

  {
    label: "Total net tax-interest expense/(income) - local currency",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", cells: [28, 44, 61, 77, 91] },
  },

  { label: "" },

  { label: "Tax-EBITDA section", type: "header" },
  {
    label:
      "Adjusted corporation tax earnings - Condition A (TIOPA 2010, s406(3))",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "cte_condition_a",
  },
  {
    label:
      "Adjusted corporation tax earnings - Condition B (TIOPA 2010, s406(4))",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "cte_condition_b",
  },
  {
    label: "CIR (Restrictions) and reactivations included in the above",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "cte_restrictions_reactivations_included",
  },
  {
    label: "Adjustments",
    editable: true,
    type: "wholeNumber",
    capMetricId: "tebitda_adjustments",
  },
  {
    label:
      "Corporation tax earnings (excluding restrictions and reactivations)",
    type: "header",
    validation: "wholeNumber",
    calculation: { type: "sum", range: { start: 96, end: 99 } },
  },

  { label: "" },

  { label: "Adjustments - s407 TIOPA 2010", type: "header" },
  {
    label: "Total net tax-interest expense/(income)",
    validation: "wholeNumber",
    calculation: { type: "reference", cell: 93 },
  },
  {
    label: "Capital allowances and charges - s407(1)(b)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_capital_allowance_charges",
  },
  {
    label: "Relevant intangible debits and credits - s407(1)(c)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_relevant_intangible_debits_credits",
  },
  {
    label:
      "Adjustment to intangible debits and credits brought in automatically (e.g. disposals above original cost)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_adjustment_intangible_debit_credits",
  },
  {
    label: "Losses from other periods - s407(1)(d)(i) and (ii)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_other_period_losses",
  },
  {
    label: "Loan relationship deficits from other periods - s407(1)(e)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_loan_relationship_deficts_other_periods",
  },
  {
    label: "Expenses of management from other periods - s407(1)(f)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_expenses_management_other_periods",
  },
  {
    label: "Group relief - s407(1)(g)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_tiopa_group_relief",
  },
  {
    label: "Group relief included above not covered by s407(1)(g)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_group_relief",
  },
  {
    label: "R&D expenditure credit - s407(3)(a)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_rd_expenditure_credit",
  },
  {
    label: "R&D additional relief - s407(3)(b)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_rd_additional_relief",
  },
  {
    label: "Deemed trading loss for pre-trading expenditure - s407(3)(c)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_trading_loss",
  },
  {
    label: "Contaminated land relief - s407(3)(d)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_contaminated_land_relief",
  },
  {
    label: "Creative industries - s407(3)(e)-(j)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_creative_industries",
  },
  {
    label:
      "Qualifying charitable donation (from current or earlier period) - s407(3)(k)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_charitable_donation",
  },
  {
    label: "Profits from patent box chargeable at a lower rate - s407(3)(l)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_patent_box_profits",
  },
  {
    label: "DTR adjustment - s409",
    validation: "wholeNumber",
    calculation: { type: "reference", cell: 141 },
  },
  {
    label: "Adjustment for election for Qualifying Infrastructure Company",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_adjustment_qualifying_infrastructure",
  },
  {
    label: "Adjustment for GAAP transition items",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_adjustment_gaap_items",
  },
  {
    label: "Finance lease depreciation - s460(1)(d)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_finance_lease_depreciation",
  },
  {
    label:
      "Reversal of right-of-use lease depreciation in respect of operating leases",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_reversal_of_lease_depreciation",
  },
  {
    label: "Long funding lease deduction - s460(1)(a)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_long_funding_lease_deduction",
  },
  {
    label: "Capital element of finance lease rentals receivable - s460(1)(c)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_finance_lease_rentals_receivable",
  },
  {
    label:
      "Long funding lessee - amount by which a deduction is reduced under section 379 of CTA 2010 - s460(1)(b)",
    editable: true,
    onesourceNonEditable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_long_funding_lessee",
  },
  {
    label:
      "Adjustment for miscellaneous losses not included in Condition B above",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_adjustment_misc_losses",
  },
  {
    label:
      "Adjustment for election to apply the Disregard Regulations to pre 1 April 2020 derivative contracts (Regulations 7, 8 and 9)",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_adjustment_disregarded_regulations",
  },
  {
    label: "Other Adjustments 1",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_other_adjustments_1",
  },
  {
    label: "Other Adjustments 2",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_other_adjustments_2",
  },
  {
    label: "Other Adjustments 3",
    editable: true,
    type: "wholeNumber",
    capMetricId: "s407_relief_other_adjustments_3",
  },
  {
    label: "Tax-EBITDA - Total for period - local currency",
    type: "header",
    validation: "wholeNumber",
    calculation: {
      type: "sum",
      range: { start: 100, end: 131, referenceCells: [119] },
    },
  },

  { label: "" },
  { label: "" },

  { label: "DTR Adjustment s409 section", type: "header" },

  { label: "" },

  {
    label: "Credit for foreign taxes under s18(2) TIOPA 2010 in GBP",
    type: "negativeWholeNumber",
    onesourceNonEditable: true,
    editable: true,
    capMetricId: "net_dtr_adjustment_gbp",
  },
  {
    label: "Credit for foreign taxes under s18(2) TIOPA 2010 in Local currency",
    type: "negativeWholeNumber",
    capMetricId: "net_dtr_adjustment_lc",
    validation: "wholeNumber",
    calculation: { type: "multiplication", factors: [8, 137] },
  },
  {
    label: "Corporation tax rate",
    type: "percentage",
    capMetricId: "corporation_tax_rate",
    notes: [
      `Where the company is subject to a different rate of corporation tax for the accounting period (e.g. due to the application of the banking surcharge or similar), the effective corporation tax rate applicable to the company for the accounting period should be entered here.`,
    ],
  },
  {
    label: "CT rate override",
    editable: true,
    type: "percentage",
    capMetricId: "ct_rate_override",
  },
  {
    label: "DTR Adjustment s409",
    type: "header",
    capMetricId: "s407_relief_dtr_adjustment_tax_rate_equal_uk",
    validation: "wholeNumber",
    calculation: { type: "dtrAdjustment" },
  },
];

const validateImportedCaps = (caps) => {
  const sheetValidator = sheetBuilder.filter(
    (row) => row.capMetricId || row.compMetricId,
  );
  caps.forEach((cap) => {
    // Validate that for all existing input rows with metricIDs (from the sheetBuilder), the provided values are valid in the imported cap
    sheetValidator.forEach(({ label, type, capMetricId, compMetricId }) => {
      const metricId = capMetricId || compMetricId;
      const val = cap[metricId];
      if (!cap.hasOwnProperty(metricId) && type !== "date") {
        // dates are optional
        throw new Error(
          `Cap for company ${cap.company_name} is missing a value for ${metricId}`,
        );
      } else if (
        (["number", "wholeNumber"].includes(type) && isNaN(Number(val))) ||
        (type === "date" && val && !processDateJS(val)) ||
        (type === "currency" && !String(val))
      ) {
        throw new Error(
          `Metric ${label} is invalid for Company ${cap.company_name ?? "Undefined Name"}`,
        );
      }
    });
  });
};

const worksheetName = "Entity Input";

// Performance optimization: Helper to apply cell validation based on row type
const applyCellValidation = (cell, row) => {
  if ([row.type, row.validation].includes("wholeNumber")) {
    setWholeNumberValidation(cell);
  } else if (row.type === "negativeWholeNumber") {
    setNegativeWholeNumberValidation(cell);
  } else if (row.type === "number") {
    if (row.capMetricId === "fx_rate") {
      setPositiveFourDecimalNumberValidation(cell);
    } else {
      setNumberValidation(cell);
    }
  } else if (row.type === "percentage") {
    setPercentageValidation(cell);
  }
};

const generateCellValue = (row, cap, company, columnLetter) => {
  // calculate and return cell value
  if (row.hasOwnProperty("calculation")) {
    const { calculation } = row;
    if (calculation.type === "sum" && calculation.hasOwnProperty("range")) {
      const { start, end, referenceCells } = calculation.range;
      return getRangeSumFormula({
        columnLetter,
        rangeStart: start,
        rangeEnd: end,
        referenceCells,
      });
    } else if (calculation.type === "sum" && calculation.cells) {
      return getListSumFormula({
        columnLetter,
        rowList: calculation.cells,
      });
    } else if (calculation.type === "reference") {
      return { formula: `${columnLetter}${calculation.cell}` };
    } else if (calculation.type === "division") {
      return {
        formula: `IFERROR(${columnLetter}${calculation.numerator}/${columnLetter}${calculation.denominator}, 0)`,
      };
    } else if (calculation.type === "multiplication") {
      const formula = calculation.factors
        .map((factor) => `${columnLetter}${factor}`)
        .join("*");
      return { formula };
    } else if (calculation.type === "dtrAdjustment") {
      return {
        formula: `TEXT(IF(OR(ISBLANK(${columnLetter}140),${columnLetter}140 =0),IFERROR(${columnLetter}138/${columnLetter}139, 0),IFERROR(${columnLetter}138/${columnLetter}140, 0)), "#,##0")`,
      };
    }
  } else if (row.hasOwnProperty("compMetricId")) {
    const val = company?.[row.compMetricId] ?? "";
    if (row.type === "date") {
      return val ? processDateJS(val)?.format("DD/MM/YYYY") : "";
    }
    return val;
  } else if (row.hasOwnProperty("capMetricId")) {
    // convert ct_rate_override to percentage (on Template) to be later restored on importing (in DataImportUtil -> extractEntityInputCapsFromExcel)
    const val =
      row.capMetricId === "ct_rate_override"
        ? (cap[row.capMetricId] ?? 0) / 100
        : row.capMetricId === "corporation_tax_rate"
          ? (cap[row.capMetricId] ?? 0.25)
          : (cap[row.capMetricId] ?? (row.type === "boolean" ? "FALSE" : 0));
    if (row.type === "date") {
      return val ? processDateJS(val)?.format("DD/MM/YYYY") : "";
    }
    return val;
  }
  return "";
};

export const handleEntityInputTemplateDownload = async ({
  isClientFacing = true,
} = {}) => {
  // Filter out OneSource extraction row for client-facing users
  const filteredSheetBuilder = isClientFacing
    ? sheetBuilder.filter((row) => row.capMetricId !== "onesource_sync")
    : sheetBuilder;

  const { workbook, worksheet } = createExcelDocument(worksheetName);

  const labels = sheetBuilder.map((row) => row.label);
  addColumnValues({
    worksheet,
    columnIndex: 1,
    values: labels,
    noHeaders: true,
  });

  const groupData = getGroupData();
  const caps = getEntityInputCaps();

  const companyMap = new Map(
    groupData?.companies?.map((comp) => [comp.company_ID, comp]) ?? [],
  );

  const columnCache = new Map();
  for (let colIndex = 0; colIndex < caps.length; colIndex++) {
    const colNum = colIndex + 2;
    const column = worksheet.getColumn(colNum);
    columnCache.set(colIndex, {
      columnNumber: colNum,
      columnLetter: column.letter,
      column: column,
    });
  }

  const cellData = [];

  caps.forEach((cap, colIndex) => {
    const company = companyMap.get(cap.company_ID);
    const colInfo = columnCache.get(colIndex);
    const columnLetter = colInfo.columnLetter;

    sheetBuilder.forEach((row, rowIndex) => {
      const rowNum = rowIndex + 1;
      const colNum = colInfo.columnNumber;

      cellData.push({
        row: rowNum,
        col: colNum,
        validation: row,
        value: generateCellValue(row, cap, company, columnLetter),
      });
    });
  });

  cellData.forEach(({ row, col, validation, value }) => {
    const cell = worksheet.getRow(row).getCell(col);
    applyCellValidation(cell, validation);
    cell.value = value;
  });

  addEntityInputTableStyles(worksheet, sheetBuilder, caps.length + 1);
  freezeTopRowAndColumnA({ worksheet, rowCount: 5 });
  addEntityInputSheetProtection(worksheet, sheetBuilder, caps);

  const tooltips = getPageTooltips("entityInput");
  const metrics = getMetrics(tooltips);
  const notes = sheetBuilder.map(
    (row) =>
      row.notes ??
      metrics.find((metric) => metric?.metric_id === row.capMetricId)
        ?.tooltipMessage,
  );
  notes.forEach((notesList, index) => {
    addNoteToCell({ worksheet, notesList, cellAddress: `A${index + 1}` });
  });

  // Hide the OneSource row for client-facing users without shifting row indices
  if (isClientFacing) {
    const onesourceRowIndex = sheetBuilder.findIndex(
      (row) => row.capMetricId === "onesource_sync",
    );
    if (onesourceRowIndex !== -1) {
      worksheet.getRow(onesourceRowIndex + 1).hidden = true;
    }
  }

  await saveExcelFile(workbook, "Entity Input Import Template");
};

export const handleEntityInputImport = async ({
  file,
  setErrors,
  data,
  setData,
  setIsLoading,
  setNewData,
  setWarnings,
  setIsConfirmModalVisible,
}) => {
  const extractErrorMessage = (err, defaultMessage) => {
    let error;
    try {
      error =
        JSON.parse(err.message)?.missingWorkSheetErrorMessage ?? defaultMessage;
    } catch {
      error = typeof err === "string" ? err : (err.message ?? defaultMessage);
    }
    return error;
  };

  try {
    const worksheet = await getWorksheetFromFile(file, worksheetName);
    const importedCaps = extractEntityInputCapsFromExcel(
      worksheet,
      sheetBuilder,
    );
    const importedData = data.map((existingCap) => {
      const existingCapID = generateCAPIdByCompName(existingCap);
      const importedCap = importedCaps.find(
        (cap) =>
          generateCAPIdByCompName(cap).localeCompare(existingCapID) === 0,
      );
      if (!importedCap)
        throw new Error(
          `Companies and Accounting Periods in the Template don't match the entries in the app.`,
        );
      // [TODO] add datavalidation on importedCap (which is the corresponding cap from the import sheet)
      return {
        ...existingCap,
        ...importedCap,
      };
    });
    validateImportedCaps(importedData);
    setData(importedData);
    return true;
  } catch (err) {
    const error = extractErrorMessage(
      err,
      "Error While Importing Entity Inputs",
    );
    setErrors([{ title: error }]);
    console.error(error);
    toast.error(error);
  }
};
